import './assets/index.css';
import { createApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import * as Sentry from '@sentry/vue';

import Landing from './views/Landing.vue';
import SignIn from './views/SignIn.vue';
import NotFound from './views/NotFound.vue';
import App from './App.vue';
import i18n from './i18n';
import AuthCallback from './views/AuthCallback.vue';

window.Sentry = Sentry;
window.SeedeEnv = {
  publicPath: '',
};

const routes = [
  { path: '/', component: Landing },
  { path: '/cn', component: Landing, props: { lang: 'zh' } },
  { path: '/fr', component: Landing, props: { lang: 'fr' } },
  { path: '/es', component: Landing, props: { lang: 'es' } },
  { path: '/ja', component: Landing, props: { lang: 'ja' } },
  { path: '/about', component: () => import('./views/About.vue') },
  { path: '/announcement', component: () => import('./views/Announcement.vue') },
  { path: '/docs', component: () => import('./views/Docs.vue') },
  { path: '/dashboard', name: 'Dashboard', component: () => import('./views/Container.vue') },
  {
    path: '/workspace',
    name: 'Workspace',
    component: () => import('./views/Workspace.vue'),
    children: [
      {
        path: '',
        name: 'Workspace.designs',
        component: () => import('./views/workspace/Design.vue'),
      },
      {
        path: 'recent',
        name: 'Workspace.recent',
        component: () => import('./views/workspace/Design.vue'),
        meta: { recentFilter: true },
      },
      {
        path: 'ai',
        name: 'Workspace.ai',
        component: () => import('./views/workspace/Ai.vue'),
      },
      {
        path: 'templates/prompt',
        name: 'Workspace.templatesPrompt',
        component: () => import('./views/workspace/Templates.vue'),
        meta: { templateTab: 'presets' },
      },
      {
        path: 'templates/featured',
        name: 'Workspace.templatesFeatured',
        component: () => import('./views/workspace/Templates.vue'),
        meta: { templateTab: 'featuredProjects' },
      },
      {
        path: 'favorites',
        name: 'Workspace.favorites',
        component: () => import('./views/workspace/Design.vue'),
      },
      {
        path: 'assets',
        name: 'Workspace.assets',
        component: () => import('./views/workspace/Asset.vue'),
      },
      {
        path: 'create',
        name: 'Workspace.create',
        component: () => import('./views/workspace/SeedeCreate.vue'),
      },
      {
        path: 'settings',
        name: 'Workspace.settings',
        component: () => import('./views/workspace/Setting.vue'),
      },
      {
        path: 'orders',
        name: 'Workspace.orders',
        component: () => import('./views/workspace/Orders.vue'),
      },
      {
        path: 'usage',
        name: 'Workspace.usage',
        component: () => import('./views/workspace/ModelUsage.vue'),
      },
      {
        path: 'trash',
        name: 'Workspace.trash',
        component: () => import('./views/workspace/Trash.vue'),
      },
      {
        path: 'libraries',
        name: 'Workspace.libraries',
        component: () => import('./views/workspace/Library.vue'),
      },
      {
        path: 'libraries/:id',
        name: 'Workspace.librariesList',
        component: () => import('./views/workspace/LibraryList.vue'),
      },
    ],
  },
  { path: '/history', name: 'History', component: () => import('./views/Container.vue') },
  { path: '/inpaint', name: 'Inpaint', component: () => import('./views/Inpaint.vue') },
  { path: '/sign-in', name: 'SignIn', component: SignIn },
  { path: '/sign-up', name: 'SignUp', component: SignIn },
  {
    path: '/accounts/reset',
    name: 'AccountsReset',
    component: () => import('./views/ForgotPassword.vue'),
  },
  { path: '/pricing', component: () => import('./views/Pricing.vue') },
  { path: '/features', component: () => import('./views/Features.vue') },
  { path: '/use-cases', component: () => import('./views/UseCases.vue') },
  { path: '/profile/:id', name: 'Profile', component: () => import('./views/User.vue') },
  { path: '/download', redirect: '/' },
  { path: '/contact', component: () => import('./views/Contact.vue') },
  { path: '/library', component: () => import('./views/Library.vue') },
  { path: '/library/:id', component: () => import('./views/LibraryView.vue') },
  { path: '/profile', component: () => import('./views/Profile.vue') },
  { path: '/invite', component: () => import('./views/Invite.vue') },
  { path: '/profile/token', name: 'APIToken', component: () => import('./views/Token.vue') },
  { path: '/tool/background-remover', component: () => import('./views/tools/RemoveBg.vue') },
  { path: '/tool/resize', component: () => import('./views/tools/Resize.vue') },
  { path: '/tool/add-border-to-photo', component: () => import('./views/tools/BorderImage.vue') },
  {
    path: '/tool/ai-image-generator',
    name: 'AIImageGenerator',
    component: () => import('./views/AIImageGenerator.vue'),
  },
  {
    path: '/tool/playground',
    name: 'Playground',
    component: () => import('./views/Playground.vue'),
  },
  { path: '/tool/browser', name: 'BrowserDebug', component: () => import('./views/Browser.vue') },
  {
    path: '/tool/viewer',
    name: 'MetadataViewer',
    component: () => import('./views/tools/Viewer.vue'),
  },
  { path: '/blog', name: 'Blog', component: () => import('./views/Blog.vue') },
  {
    path: '/blog/create',
    name: 'createBlog',
    component: () => import('./views/CreateArticle.vue'),
  },
  {
    path: '/blog/edit/:id',
    name: 'EditBlog',
    component: () => import('./views/CreateArticle.vue'),
  },
  { path: '/blog/:id', name: 'BlogContent', component: () => import('./views/Article.vue') },
  { path: '/create', name: 'Create', component: () => import('./views/Create.vue') },
  { path: '/create2', name: 'Create2', component: () => import('./views/Create2.vue') },
  {
    path: '/create/brief',
    name: 'CreateBrief',
    component: () => import('./views/brief/CreateBrief.vue'),
  },
  {
    path: '/create/alpha',
    name: 'CreateAlpha',
    props: { alpha: true },
    component: () => import('./views/Create.vue'),
  },
  {
    path: '/create/alpha/:id',
    name: 'CreateAlphaById',
    props: { alpha: true },
    component: () => import('./views/Create.vue'),
  },
  {
    path: '/create/pro',
    name: 'CreatePro',
    props: { pro: true },
    component: () => import('./views/Create.vue'),
  },
  { path: '/create/:id', name: 'CreateById', component: () => import('./views/Create.vue') },
  { path: '/preview', name: 'Preview', component: () => import('./views/Preview.vue') },
  { path: '/affiliates', name: 'Affiliates', component: () => import('./views/Affiliates.vue') },
  { path: '/terms', name: 'Terms', component: () => import('./views/Terms.vue') },
  { path: '/privacy', name: 'Privacy', component: () => import('./views/Privacy.vue') },
  { path: '/cookies', name: 'Cookies', component: () => import('./views/Cookies.vue') },
  { path: '/help', component: () => import('./views/Help.vue') },
  { path: '/redeem', name: 'Redeem', component: () => import('./views/Redeem.vue') },
  { path: '/design/:id', name: 'Design', component: () => import('./views/Design.vue') },
  {
    path: '/design-embed/:id',
    name: 'DesignEmbed',
    component: () => import('./views/DesignEmbed.vue'),
  },
  { path: '/design-embed-test/:id', component: () => import('./views/DesignEmbedTest.vue') },
  { path: '/success', name: 'Success', component: () => import('./views/Success.vue') },
  { path: '/space/assets', name: 'Assets', component: () => import('./views/Assets.vue') },
  { path: '/templates', name: 'Templates', component: () => import('./views/Templates.vue') },
  {
    path: '/templates/tag/:tag',
    name: 'TemplatesTag',
    component: () => import('./views/Templates.vue'),
  },
  {
    path: '/template/:id',
    name: 'TemplatesDetail',
    component: () => import('./views/TemplateDetail.vue'),
  },
  { path: '/_/admin', component: () => import('./views/Admin.vue') },
  { path: '/_/scene', component: () => import('./views/AdminScene.vue') },
  { path: '/_/admin/redemption', component: () => import('./views/admin/Redemption.vue') },
  { path: '/_/admin/template', component: () => import('./views/admin/Template.vue') },
  { path: '/auth/callback', component: AuthCallback },
  { path: '/:pathMatch(.*)*', component: NotFound },
];
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});
const app = createApp(App);
const isDev = process.env.NODE_ENV === 'development';
if (!isDev) {
  Sentry.init({
    app,
    dsn: 'https://b81d4c569bf9d040f9252aff8cdba0a8@o4507497185017856.ingest.us.sentry.io/4508597057093632',
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      // Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event) {
      // Ignore image loading errors
      if (event.exception?.values?.[0]?.value?.includes('Failed to load image')) {
        return null;
      }
      // Ignore errors from localhost
      if (event.exception?.values?.[0]?.value?.includes('localhost')) {
        return null;
      }
      // Ignore benign ResizeObserver loop errors
      if (event.exception?.values?.[0]?.value?.includes('ResizeObserver loop limit exceeded')) {
        return null;
      }
      return event;
    },
  });
}

window.Sentry = Sentry;

app.use(router);
app.use(i18n);
app.mount('#app');
